import {
  Box,
  Modal,
  Grid,
  Button,
  Switch,
  Divider,
  Typography,
  FormControlLabel,
} from '@mui/material';
import ICTable from './ICTable';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { apiCall } from '../../utils/action';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import "../../../assets/styles/containers/_importComments.scss";
import { API_CONSTANTS, postApiCall } from '../../config/api-constants';
import { CheckIcon, RefreshIcon } from '../../../assets/svgIcons/Svg';
import { getNNTagList } from '../../store/action';
import { useDispatch } from 'react-redux';

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  marginRight: 5,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': { width: 15 },
    '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(9px)' },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#239D80',
        ...theme.applyStyles('dark', { backgroundColor: '#239D80' }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], { duration: 200 }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', { backgroundColor: 'rgba(255,255,255,.35)' }),
  },
}));

const ImportComments = ({
  open,
  onClose,
  data,
  closeOptionModal,
  importing,
  setImporting,
  setProgress,
  importType = "comments",
  onImportSuccess,
  onRetryClick
}) => {
  const [removeAfterImport, setRemoveAfterImport] = useState(false);
  const [mergeIdentical, setMergeIdentical] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [lastFailedOrders, setLastFailedOrders] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    if (importType === "lists") {
      setMergeIdentical(true);
    }
  }, [importType]);

  const getTitle = () => {
    return importType === "lists"
      ? "Import lists from templates"
      : "Import comments from templates";
  };

  const getDescription = () => {
    return importType === "lists"
      ? "Select which templates you would like to include in the import. Lists will be imported from the last published version."
      : "Select which templates you would like to include in the import. Comments will be imported from the last published version.";
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Wrap the existing apiCall in a Promise we can await
  const importOne = (order) =>
    new Promise((resolve) => {
      const formdata = {
        company_id: sessionStorage.getItem("company_id"),
        template_id: order?.id,
        merge_identical_comment: mergeIdentical,
      };

      apiCall(
        (response) => {
          const ok = !!response?.data?.success;
          if (ok) {
            if (removeAfterImport) {
              removeCommentsAfterImportAPI(order);
            }
            resolve({ ok: true, imported_count: response?.data?.imported_count || 0 });
          } else {
            resolve({
              ok: false,
              error: response?.data?.message || "Failed",
              order,
            });
          }
        },
        formdata,
        "IMPORT_COMMENTS"
      );
    });

  const removeCommentsAfterImportAPI = (order) => {
    const formdata = {
      company_id: sessionStorage.getItem("company_id"),
      template_id: order?.id
    };
    apiCall(() => { }, formdata, "REMOVE_COMMENTS_AFTER_MERGE");
  };

  const importList = async (order) => {
    const formdata = {
      company_id: sessionStorage.getItem("company_id"),
      template_id: order?.id,
    };

    const response = await postApiCall(API_CONSTANTS.IMPORT_LISTS, formdata);
    const ok = !!response?.data?.success;
    // Only for lists: get counts from response
    const importedCount = importType === "lists" ? (response?.data?.imported_count || 0) : 0;
    const mergedCount = importType === "lists" ? (response?.data?.merged_count || 0) : 0;

    return ok ? {
      ok: true,
      importedCount,
      mergedCount,
      order
    } : {
      ok: false,
      order,
      importedCount: 0,
      mergedCount: 0
    };
  };

  const showSummaryToast = (totalTemplates, failedOrders, totalImported, totalMerged, onRetry) => {
    const failed = failedOrders.length;
    const successfulTemplates = totalImported - failed;
    if (failed === 0) {
      toast.custom((t) => (
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexDirection: 'column',
            background: '#10A957',
            color: '#FFFFFF',
            padding: '16px',
            borderRadius: '8px',
            width: "437px",
            height: "auto",
            minHeight: importType === "lists" ? "116px" : "auto",
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
            maxWidth: '380px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckIcon />
            <span style={{ fontWeight: 700 }}>
              Import complete
            </span>

            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                color: '#fff',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center'
              }}
              aria-label="Dismiss"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          {/* Simple message for lists, detailed for comments */}
          {importType === "lists" ? (
            // GlobalList: Show imported - merged count for lists only
            <span style={{ fontWeight: 700, marginRight: "10px" }}>
              {importType === "lists" ? (totalImported - totalMerged) || 0 : data?.length || 0} lists have been created from {totalTemplates - failed} templates.
              {importType === "lists" && totalMerged > 0 && ` (${totalMerged} identical lists were merged)`}
            </span>
          ) : (
            // CommentLibraryTable: Keep original logic for comments
            <>
              <span style={{ fontWeight: 700, marginRight: "10px" }}>
                {totalImported} comments have been created from {totalTemplates - failed} templates.
              </span>

              {/* <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', opacity: 0.9 }}>
                {data?.slice(0, 3).map((template) => (
                  <span key={template.id} style={{
                    border: '1px solid rgba(255,255,255,0.6)',
                    borderRadius: 14,
                    padding: '2px 8px',
                    fontSize: 12,
                    background: 'rgba(255,255,255,0.1)'
                  }}>
                    {template.name || template.title || `Template #${template.id}`}
                  </span>
                ))}
                {data?.length > 3 && (
                  <span style={{ fontSize: 12 }}>+{data.length - 3} more templates</span>
                )}
              </div> */}
            </>
          )}
        </div>
      ));
      return;
    }

    // ERROR CASE - Import incomplete with Retry button
    toast.custom((t) => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: '#FF9432',
          color: '#FFFFFF',
          padding: '16px',
          borderRadius: '8px',
          width: "437px",
          minHeight: "116px",
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
          maxWidth: '380px',
        }}
      >
        {/* Header row with icon, title, and close button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningAmberIcon style={{ fontSize: '20px' }} />
            <span style={{ fontWeight: 700, fontSize: '14px' }}>
              Import incomplete
            </span>
          </div>

          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              color: '#fff',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '0'
            }}
            aria-label="Dismiss"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Error message */}
        <div style={{
          marginBottom: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '1.4'
          }}>
            {importType === "lists"
              ? `${importType === "lists" ? (totalImported - totalMerged) || 0 : successfulTemplates} lists have been created from ${successfulTemplates || failed} templates.` : `${failed} out of ${totalTemplates} failed`}
          </span>

          {importType === "lists" && (
            <span style={{
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '1.4'
            }}>
              {failed} lists had errors.
              {importType === "lists" && totalMerged > 0 && ` (${totalMerged} identical lists were merged)`}
            </span>
          )}
        </div>

        {/* Failed items tags */}
        <div style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: '16px',
          opacity: 0.9
        }}>
          {failedOrders.slice(0, 5).map((o) => (
            <span key={o.id} style={{
              border: '1px solid rgba(255,255,255,0.6)',
              borderRadius: 14,
              padding: '2px 8px',
              fontSize: 12,
              background: 'rgba(255,255,255,0.1)'
            }}>
              {o.name || o.title || `#${o.id}`}
            </span>
          ))}
          {failedOrders.length > 5 && (
            <span style={{ fontSize: 12 }}>+{failedOrders.length - 5} more</span>
          )}
        </div>

        {/* Retry button aligned to right */}
        {importType === "lists" &&
          <div>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                onRetryClick?.();
              }}
              style={{
                background: 'transparent',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 0px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <RefreshIcon /> Retry
            </button>
          </div>
        }
      </div>
    ));
  };

  const runImportBatch = async (orders) => {
    const total = orders.length;
    let completed = 0;
    const failedOrders = [];
    let totalImported = 0;
    let totalMerged = 0;

    setProgress(0);

    for (let order of orders) {
      let result;

      if (importType === "lists") {
        result = await importList(order);
      } else {
        result = await importOne(order);
      }
      if (!result.ok) {
        failedOrders.push(order);
      } else {
        // Only accumulate counts for lists
        if (importType === "lists") {
          totalImported += result.importedCount || 0;
          totalMerged += result.mergedCount || 0;
        } else {
          totalImported += result.imported_count || 0;
          totalMerged += result.merged_count || 0;
        }
      }
      completed++;
      setProgress(Math.round((completed / total) * 100));
      await sleep(300);
    }
    return {
      failedOrders,
      totalImported,
      totalMerged
    };
  };

  const handleImport = async () => {
    if (!selectedOrders.length || importing) return;
    setImporting(true);
    setProgress(0);
    onClose();
    closeOptionModal?.();
    const { failedOrders, totalImported, totalMerged } = await runImportBatch(selectedOrders);
    setImporting(false);
    setLastFailedOrders(failedOrders);
    showSummaryToast(
      selectedOrders.length,
      failedOrders,
      totalImported,
      totalMerged,
      () => retryFailed(failedOrders)
    );
    if (failedOrders.length === 0) {
      setSelectedOrders([]);
      setMergeIdentical(true);
      setRemoveAfterImport(false);
      onImportSuccess?.();
    }
    dispatch(getNNTagList());
  };

  const retryFailed = async (toRetry = lastFailedOrders) => {
    if (!toRetry?.length) return;

    setImporting(true);
    setProgress(0);

    const { failedOrders: failedAgain, totalImported, totalMerged } = await runImportBatch(toRetry);

    setImporting(false);
    setLastFailedOrders(failedAgain);

    // Show toast with counts for retry (counts will be 0 for comments)
    showSummaryToast(
      toRetry.length,
      failedAgain,
      totalImported,
      totalMerged,
      () => retryFailed(failedAgain)
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} className='ic-main'>
        <Box className='ic-modal'>
          <Grid container sx={{ p: "16px" }}>
            <Grid item xs={8}>
              <Typography className='ic_title'>
                {getTitle()}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <CloseIcon className='ic_close_icon' onClick={onClose} />
            </Grid>
          </Grid>
          <Divider />
          <Grid container sx={{ p: "24px 32px" }}>
            <Grid item xs={12}>
              <Typography className='ic_body'>
                {getDescription()}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ICTable
                data={data}
                selectedOrders={selectedOrders}
                setSelectedOrders={setSelectedOrders}
              />
            </Grid>
          </Grid>
          <Divider />
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: "16px", gap: "10px" }}>
            <Box sx={{ ml: 2, gap: "10px" }}>
              {/* CHANGED: Show different UI based on importType */}
              {importType === "comments" ? (
                // For Comments tab: Show toggle switch
                <FormControlLabel
                  control={
                    <AntSwitch
                      checked={mergeIdentical}
                      onChange={() => setMergeIdentical(!mergeIdentical)}
                    />
                  }
                  label={<Typography className='ic_checked'>
                    Merge identical comments
                  </Typography>}
                />
              ) : (
                // For Lists tab: Show static text (no toggle)
                <Typography className='ic_checked' sx={{ color: '#000000', fontSize: '14px', marginLeft: '8px' }}>
                  Identical lists will be merged automatically
                </Typography>
              )}
            </Box>
            <Box>
              <Button
                onClick={() => { onClose(); setSelectedOrders([]); setMergeIdentical(true); }}
                sx={{ mr: 2 }}
                className='ic-cancle-button'
                disabled={importing}
              >
                Cancel
              </Button>
              <Button
                disabled={selectedOrders.length === 0 || importing}
                onClick={handleImport}
                className="default-btn teams-detail-btn"
              >
                Begin import
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default ImportComments;